const STORAGE_KEY = 'quickProductQueue:v1';

export const quickProductKey = (row) => keyFor(row);

const keyFor = (row) =>
  String(row?.partNumber || row?.productNumber || '')
    .toUpperCase()
    .replace(/[\s\-_.]/g, '');

export const readLocalQuickProducts = () => {
  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

// Keeps a browser-local copy as a fallback when the dashboard is temporarily
// connected to an older server that does not yet expose the queue endpoints.
export const saveLocalQuickProducts = (items) => {
  const existing = new Map(readLocalQuickProducts().map((item) => [keyFor(item.row), item]));
  const now = new Date().toISOString();

  items.forEach((item) => {
    const key = keyFor(item.row);
    if (!key) return;
    existing.set(key, {
      ...existing.get(key),
      ...item,
      _id: `local-${key}`,
      status: 'PENDING',
      fetchedAt: now,
    });
  });

  const next = [...existing.values()];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
};

export const markLocalQuickProductsUploaded = (ids) => {
  const wanted = new Set(ids);
  const next = readLocalQuickProducts().map((item) =>
    wanted.has(item._id) ? { ...item, status: 'UPLOADED', uploadedAt: new Date().toISOString() } : item
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
};

export const removeLocalQuickProduct = (id, row) => {
  const rowKey = keyFor(row);
  const next = readLocalQuickProducts().filter((item) => item._id !== id && (!rowKey || keyFor(item.row) !== rowKey));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
};

// --- Hand-off to Bulk Upload: products stay "Ready" until the catalog upload succeeds ---
const UPLOAD_KEY = 'quickProductUploadIds';

export const rememberQuickProductsForUpload = (items) => {
  const pending = items.map((item) => ({
    id: item._id,
    local: String(item?._id || '').startsWith('local-'),
    key: keyFor(item.row),
  }));
  sessionStorage.setItem(UPLOAD_KEY, JSON.stringify(pending));
};

/**
 * Called after Bulk Upload succeeds: moves the quick products that were in the
 * uploaded list to "Uploaded to catalog". Does nothing for other bulk uploads.
 */
export const markQuickProductsAfterUpload = async (api, uploadedProducts = []) => {
  let pending = [];
  try {
    pending = JSON.parse(sessionStorage.getItem(UPLOAD_KEY) || '[]');
  } catch {
    pending = [];
  }
  sessionStorage.removeItem(UPLOAD_KEY);
  if (!Array.isArray(pending) || !pending.length) return;

  const uploadedKeys = new Set(
    (uploadedProducts || [])
      .flatMap((p) => [].concat(p?.productOverview?.productNumber || p?.productNumber || p?.partNumber || []))
      .map((n) => keyFor({ partNumber: n }))
      .filter(Boolean)
  );
  const done = pending.filter((p) => p.key && uploadedKeys.has(p.key));
  if (!done.length) return;

  const serverIds = done.filter((p) => !p.local).map((p) => p.id);
  if (serverIds.length) {
    try {
      await api.markQuickProductsUploaded(serverIds);
    } catch (err) {
      console.log(err);
    }
  }
  markLocalQuickProductsUploaded(done.map((p) => (p.local ? p.id : `local-${p.key}`)));
};
